import { Text, View } from '@react-pdf/renderer'
import type { Listing, ListingSpace } from '~/models'
import { tw } from './tw'

import { cn, fmtNumberAbbr } from '~/lib'

const RATE_TYPES: { [key: string]: string } = {
  sf_year: '/SF/Year',
  month: '/Month',
  year: '/Year',
  sf_month: '/SF/Month',
}

function getLeaseSpaceRows({
  listing,
  isNumAbbr = false,
}: {
  listing: Listing
  isNumAbbr?: boolean
}) {
  const spaces = (listing.suites || []).filter((s) => s.available)
  spaces.sort((a, b) => (a.unit || '').localeCompare(b.unit || ''))

  if (spaces.length === 0) {
    return { headers: [], rows: {} }
  }

  const maybeFmtNumberAbbr = (value?: number) => {
    if (isNumAbbr) {
      return fmtNumberAbbr(value)
    }
    return value?.toLocaleString()
  }

  const getSpaceRate = (space: ListingSpace) => {
    const min = space.rate_min
    const max = space.rate_max
    const rateType =
      space.rate_type && typeof RATE_TYPES[space.rate_type] !== 'undefined'
        ? ` ${RATE_TYPES[space.rate_type]}`
        : ''
    if (!min && !max) {
      return 'Negotiable'
    } else if (min && !max) {
      return `$${maybeFmtNumberAbbr(min)} ${rateType}`
    } else if (min && max && min !== max) {
      return `$${maybeFmtNumberAbbr(min)} - $${maybeFmtNumberAbbr(
        max
      )}${rateType}`
    }
    return `$${maybeFmtNumberAbbr(min)}${rateType}`
  }

  const getSquareFootage = (space: ListingSpace) => {
    const min = space.min_available_sf
    const max = space.max_available_sf
    if (!min && !max) {
      return null
    } else if (min !== max) {
      return `${maybeFmtNumberAbbr(min)} - ${maybeFmtNumberAbbr(max)} SF`
    }
    return `${maybeFmtNumberAbbr(min)} SF`
  }

  const rows: { [key: string]: any } = {}
  const headers: { key: string; label: string }[] = []

  spaces.forEach((space) => {
    rows[space.id] = {
      name: space.unit,
      rate: getSpaceRate(space),
      footage: getSquareFootage(space),
      acres: space.acres
        ? space.acres
            .toLocaleString(undefined, { minimumFractionDigits: 4 })
            .replace(/(\.\d*?[1-9])0+$/, '$1')
            .replace('.0000', '') + ' ac'
        : '',
      sublease: space.sublease ? 'Yes' : '',
    }
  })

  const spaceHeaders = [
    { key: 'name', label: 'Name' },
    { key: 'rate', label: 'Rate' },
    { key: 'footage', label: 'Footage' },
    { key: 'acres', label: 'Acres' },
    { key: 'sublease', label: 'Sublease' },
    { key: 'available', label: 'Available' },
  ]
  spaceHeaders.forEach((header) => {
    if (Object.values(rows).some((row) => row[header.key])) {
      headers.push(header)
    }
  })

  return { headers, rows }
}

export default function PdfLeaseSpaces({ listing }: { listing: Listing }) {
  const { headers, rows } = getLeaseSpaceRows({ listing, isNumAbbr: false })

  if (headers.length === 0) {
    return null
  }

  return (
    <View style={tw('mt-4')} break={false}>
      <Text style={tw('font-heading mb-4 text-base leading-none')}>
        Lease Spaces
      </Text>
      <View style={tw('flex flex-row')}>
        {headers.map((header, index) => (
          <View key={header.key} style={index === 0 ? tw('flex-1') : undefined}>
            <View style={tw('border-b border-slate-200 pb-1 pr-2')}>
              <Text style={tw('font-sans text-sm font-medium')}>
                {header.label}
              </Text>
            </View>
            {listing.suites!.map(({ id }) => {
              if (typeof rows[id] === 'undefined') {
                // not available
                return null
              }
              return (
                <View key={id} style={tw('py-1 pr-4')}>
                  <Text
                    style={tw(
                      cn('font-sans text-sm', index === 0 && 'font-medium')
                    )}>
                    {rows[id][header.key]}
                  </Text>
                </View>
              )
            })}
          </View>
        ))}
      </View>
    </View>
  )
}
